import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBookingById } from '@services/reservasService';
import { deleteBooking } from '@services/bookingService';
import { getParkerByLocation } from '@services/parkerService';
import { turnOnCharger, turnOffCharger, getChargersByParker, getChargerSummary } from '@services/chargerService';
import { getWalletByUserId, getTransactionByBookingId } from '@services/financeService';
import { getTariffs } from '@services/tariffService';
import { getFactusUserData } from '@services/facturationService';
import { AuthContext } from '@context/AuthContext';
import { Box, Typography, Button, Paper, Grid, CircularProgress, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import EvStationIcon from '@mui/icons-material/EvStation';
import CancelBookingModal from './CancelBookingModal';
import api from '@utils/api';
import { TUYA_ENERGY_CORRECTION_FACTOR, correctKwh } from '@utils/energyCorrections';

const formatBookingPeriodTo12Hour = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };

    const formattedStart = start.toLocaleString('es-CO', options);
    const formattedEnd = end.toLocaleString('es-CO', options);

    return `${formattedStart} - ${formattedEnd}`;
  } catch (e) {
    return 'Fechas inválidas';
  }
};

const DetalleReserva = () => {
  const { bookingId } = useParams();
  const { user } = useContext(AuthContext);
  const [reserva, setReserva] = useState(null);
  const navigate = useNavigate();
  const [charger, setCharger] = useState(null);
  const [parkerId, setParkerId] = useState(null);
  const [isChargerOn, setIsChargerOn] = useState(() => {
    const storedState = localStorage.getItem(`chargerState_${bookingId}`);
    return storedState ? JSON.parse(storedState) : false;
  });
  const [dineroDisponible, setDineroDisponible] = useState(0);
  const [cargaEntregada, setCargaEntregada] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chargerError, setChargerError] = useState(null);
  const [walletError, setWalletError] = useState(null);
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isTurnOffModalOpen, setIsTurnOffModalOpen] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [bookingStatus, setBookingStatus] = useState('pending');
  const [activeTariff, setActiveTariff] = useState(null);
  const [isChargeFinished, setIsChargeFinished] = useState(() => {
    const storedState = localStorage.getItem(`chargeFinished_${bookingId}`);
    return storedState ? JSON.parse(storedState) : false;
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasFacturationData, setHasFacturationData] = useState(false);

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const reservaData = await getBookingById(bookingId);
      const currentReserva = reservaData.data;
      setReserva(currentReserva);
      if (!currentReserva.location) throw new Error("La reserva no tiene una ubicación asignada.");

      const parkerData = await getParkerByLocation(currentReserva.location);

      if (!parkerData.data || parkerData.data.length === 0) throw new Error("No se encontró el parqueadero para esta ubicación.");
      const parker = parkerData.data[0]; // Acceder al primer elemento del array
      const currentParkerId = parker.parker_id;
      if (!currentParkerId) throw new Error("El parqueadero no tiene un ID válido.");
      setParkerId(currentParkerId);

      const chargersData = await getChargersByParker(currentParkerId);
      if (!chargersData || chargersData.length === 0) throw new Error("No se encontró un cargador para este parqueadero.");
      const currentCharger = chargersData;
      setCharger(currentCharger);

      const tariffsData = await getTariffs({ place_id: parker.place_id });
      if (tariffsData && tariffsData.data) {
        const active = tariffsData.data.find(t => t.is_active);
        setActiveTariff(active);
      }

      if (currentReserva && currentReserva.user_id) {
        try {
          const wallet = await getWalletByUserId(currentReserva.user_id);
          const balance = parseFloat(wallet.balance) || 0;
          const creditLimit = parseFloat(wallet.credit_limit) || 0;
          const availableMoney = balance + creditLimit;
          setDineroDisponible(availableMoney);
        } catch (walletError) {
          console.error('Error fetching wallet:', walletError);
          setWalletError('No se pudo cargar la información de la billetera.');
        }
      }
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError(err.message || 'Error al cargar los detalles de la reserva.');
    } finally {
      setLoading(false);
    }
  }, [bookingId, user]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    let interval;

    const fetchChargerStatus = async () => {
      if (charger && charger && reserva) {
        try {
          const summaryData = await getChargerSummary(charger[0].charger_id);
          if (reserva.start_total_energy_kwh !== null && reserva.start_total_energy_kwh !== undefined) {
            if (charger[0].controller_type === 'wallbox') {
              setIsChargerOn(summaryData.is_active);
              setCargaEntregada(summaryData.session_energy_kwh || 0);
            } else if (charger[0].controller_type === 'tuya') {
              setIsChargerOn(summaryData.status === 'On');
              const factor = (typeof summaryData?.energy_correction_factor === 'number')
                ? summaryData.energy_correction_factor
                : TUYA_ENERGY_CORRECTION_FACTOR;

              const totalCorrected = summaryData?.energy_is_corrected
                ? (summaryData.total_energy_kwh || 0)
                : correctKwh((summaryData.total_energy_kwh || 0), factor);

              // start_total_energy_kwh viene de backend/DB y históricamente puede estar en lectura cruda.
              // Para Tuya lo llevamos al mismo "espacio" corregido para que el delta sea consistente.
              const startCorrected = correctKwh((reserva.start_total_energy_kwh || 0), factor);

              const consumedEnergy = totalCorrected - startCorrected;
              setCargaEntregada(consumedEnergy > 0 ? consumedEnergy : 0);
            }} else {
            setCargaEntregada(0);
          }

        } catch (pollingError) {
          console.error("Error durante el polling de datos del cargador:", pollingError);
        }
      }
    };

    fetchChargerStatus();

    if (bookingStatus === 'active' && !isChargeFinished) {
      interval = setInterval(fetchChargerStatus, 60000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [charger, reserva, bookingStatus, isChargeFinished]);

  const handleTurnOn = useCallback(async () => {
    if (charger && charger) {
      try {
        await turnOnCharger(charger[0].charger_id);
        setIsChargerOn(true);
        localStorage.setItem(`chargerState_${bookingId}`, JSON.stringify(true));
        setNotification({ message: 'Carga iniciada con éxito', type: 'success' });
      } catch (err) {
        const errorMessage = err.response?.data?.detail || 'No se pudo activar la carga, comunícate con el administrador.';
        setNotification({ message: errorMessage, type: 'error' });
      }
      setTimeout(() => setNotification({ message: '', type: '' }), 8000);
    }
  }, [charger, bookingId]);

  const confirmTurnOff = async () => {
    if (charger && charger) {
      try {
        await turnOffCharger([charger[0].charger_id]);
        setIsChargerOn(false);
        localStorage.setItem(`chargerState_${bookingId}`, JSON.stringify(false));
        setNotification({ message: 'Carga finalizada con éxito', type: 'success' });

        if (charger.controller_type === 'wallbox') {
          setIsChargeFinished(true);
          localStorage.setItem(`chargeFinished_${bookingId}`, JSON.stringify(true));
          setBookingStatus('finished');
        }
      } catch (err) {
        setNotification({ message: 'No se pudo finalizar la carga, comunícate con el administrador.', type: 'error' });
      }
      setTimeout(() => setNotification({ message: '', type: '' }), 5000);
    }
    setIsTurnOffModalOpen(false);
  };

  const handleTurnOff = () => {
    if (charger && charger && charger.controller_type === 'wallbox') {
      setIsTurnOffModalOpen(true);
    } else {
      confirmTurnOff();
    }
  };

  const handleCancelBooking = () => {
    setIsCancelModalOpen(true);
  };

  const handleCloseCancelModal = (shouldRefresh) => {
    setIsCancelModalOpen(false);
    if (shouldRefresh) {
      navigate('/movilidadElectrica/mis-reservas');
    }
  };

  useEffect(() => {
    const checkBookingStatus = () => {
      if (!reserva || !reserva.start_date || !reserva.end_date) {
        return;
      }
      try {
        const now = new Date();
        const nowUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds());

        const startDate = new Date(reserva.start_date);
        const endDate = new Date(reserva.end_date);

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          return;
        }

        if (nowUtc >= endDate.getTime() || reserva.status == "completed") {
          if (bookingStatus !== 'finished') {
            setBookingStatus('finished');
            const finalizeBooking = async () => {
              try {
                const txnData = await getTransactionByBookingId(bookingId);
                setTransaction(txnData);
                const updatedBookingData = await getBookingById(bookingId);
                if (updatedBookingData && updatedBookingData.data) {
                  setReserva(updatedBookingData.data);
                  setCargaEntregada(updatedBookingData.data.kwh_consumed || 0);
                }
              } catch (err) {
                console.error("Error finalizing booking or fetching transaction:", err);
              }
            };
            finalizeBooking();
          }
        } else if (nowUtc >= startDate.getTime() && nowUtc < endDate.getTime()) {
          setBookingStatus('active');
        } else {
          setBookingStatus('pending');
        }
      } catch (e) {
        console.error("Error checking booking status:", e);
      }
    };

    const interval = setInterval(checkBookingStatus, 1000);
    return () => clearInterval(interval);
  }, [reserva, isChargerOn, bookingId, bookingStatus]);

  useEffect(() => {
    const checkFacturationData = async () => {
      try {
        const facturationData = await getFactusUserData();
        setHasFacturationData(!!facturationData);
      } catch (error) {
        console.error("Error al verificar los datos de facturación:", error);
        setHasFacturationData(false);
      }
    };

    if (bookingStatus === 'finished') {
      checkFacturationData();
    }
  }, [bookingStatus]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!reserva) return <p>No se encontró la reserva.</p>;

  if (bookingStatus === 'finished') {
    return (
      <Box sx={{ padding: 3, textAlign: 'center' }}>
        <Paper elevation={3} sx={{ padding: 3 }}>
          <Typography variant="h5">Reserva Finalizada</Typography>
          {transaction ? (
            <Box>
              <Typography>Costo Total: ${new Intl.NumberFormat('es-CO').format(transaction.amount)}</Typography>
              <Typography>Energía Consumida: {reserva.kwh_consumed !== null && reserva.kwh_consumed !== undefined ? `${reserva.kwh_consumed.toFixed(2)} kWh` : 'N/A'}</Typography>
              {reserva && reserva.start_date && reserva.end_date && (
                <Typography>
                  Tiempo de Reserva: {(() => {
                    const startDate = new Date(reserva.start_date);
                    const endDate = new Date(reserva.end_date);
                    const durationMs = endDate - startDate;
                    const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
                    const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
                    return `${durationHours}h ${durationMinutes}m`;
                  })()}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography>El tiempo de tu reserva ha concluido.</Typography>
          )}
          {bookingStatus === 'finished' &&
            (hasFacturationData ? (
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  setIsDownloading(true);
                  try {
                    const token = sessionStorage.getItem('token');
                    const response = await fetch(`${api.defaults.baseURL}/facturation/booking/${bookingId}/invoice`, {
                      headers: {
                        'Authorization': `Bearer ${token}`,
                      },
                    });
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `factura_${bookingId}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                  } catch (error) {
                    console.error("Error al descargar la factura:", error);
                  } finally {
                    setIsDownloading(false);
                  }
                }}
                sx={{ mt: 2 }}
                disabled={isDownloading}
              >
                {isDownloading ? <CircularProgress size={24} /> : 'Descargar Factura'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate('/facturacion')}
                sx={{ mt: 2 }}
              >
                Ingresar Datos de Facturación
              </Button>
            ))}
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      {notification.message && <Alert severity={notification.type} sx={{ mb: 2 }}>{notification.message}</Alert>}
      {chargerError && <Alert severity="warning" sx={{ mb: 2 }}>{chargerError}</Alert>}
      {walletError && <Alert severity="error" sx={{ mb: 2 }}>{walletError}</Alert>}
      {activeTariff && (
        <Alert severity="info" sx={{ mb: 2 }}>
          El precio actual por kWh es de ${activeTariff.price_per_kwh}.
        </Alert>
      )}
      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6">
          Dinero disponible para carga eléctrica: ${new Intl.NumberFormat('es-CO').format(dineroDisponible)}
        </Typography>
      </Paper>
      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={8}>
            <Typography variant="h6">Cargador semi rápido {reserva.location}</Typography>
            <Typography>El espacio reservado es de {formatBookingPeriodTo12Hour(reserva.start_date, reserva.end_date)}</Typography>
            {charger[0].controller_type === 'wallbox' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Aviso: Si desconecta su vehículo, la sesión de carga finalizará y no podrá reanudarse.
              </Alert>
            )}
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'center' }}>
            {isChargerOn ? (
              <Button
                variant="contained"
                color="error"
                onClick={handleTurnOff}
                disabled={bookingStatus !== 'active'}
                sx={{ borderRadius: '50%', width: 80, height: 80 }}
              >
                <PowerSettingsNewIcon sx={{ fontSize: 40 }} />
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleTurnOn}
                disabled={bookingStatus !== 'active' || (charger && charger && charger.controller_type === 'wallbox' && isChargeFinished)}
                sx={{ borderRadius: '50%', width: 80, height: 80 }}
              >
                <PowerSettingsNewIcon sx={{ fontSize: 40 }} />
              </Button>
            )}
            <Typography>
              {bookingStatus === 'active'
                ? (isChargerOn ? 'Carga en curso. Haz clic para finalizar.' : 'Dar clic para iniciar la carga')
                : 'Tu reserva aún no está activa'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={2}>
            <EvStationIcon sx={{ fontSize: 40, color: 'green' }} />
          </Grid>
          <Grid item xs={6}>
            <Typography>Costo estimado de la reserva</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h5">
              {reserva.start_date && reserva.end_date && activeTariff
                ? `$${new Intl.NumberFormat('es-CO').format((
                    ((new Date(reserva.end_date) - new Date(reserva.start_date)) / 3600000) * (activeTariff.price_per_hour + activeTariff.price_per_kwh * 6.5)) + activeTariff.connection_fee
                  )}`
                : 'Calculando...'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={2}>
            <EvStationIcon sx={{ fontSize: 40, color: 'green' }} />
          </Grid>
          <Grid item xs={6}>
            <Typography>Carga entregada</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h5">{cargaEntregada.toFixed(2)} kWh</Typography>
          </Grid>
        </Grid>
      </Paper>
      {bookingStatus !== 'finished' && (
        <Button
          variant="outlined"
          color="error"
          onClick={handleCancelBooking}
          sx={{ mt: 2 }}
          disabled={bookingStatus === 'active'}
        >
          Cancelar Reserva
        </Button>
      )}
      {reserva && (
        <CancelBookingModal
          isOpen={isCancelModalOpen}
          onClose={handleCloseCancelModal}
          booking={reserva}
          setNotification={setNotification}
        />
      )}
      <Dialog
        open={isTurnOffModalOpen}
        onClose={() => setIsTurnOffModalOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmar finalización de carga"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Estás a punto de finalizar la carga. Una vez finalizada, no podrás volver a iniciarla en esta sesión. ¿Estás seguro de que deseas continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTurnOffModalOpen(false)}>Cancelar</Button>
          <Button onClick={confirmTurnOff} autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DetalleReserva;
